import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SongCard from '../components/SongCard';
import { Search, Music, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = [
  'All',
  'Worship',
  'Gospel',
  'Contemporary Christian',
  'Praise',
  'Hymns',
  'Christian Rock',
  'Christian Hip Hop',
  'Devotional',
  'Instrumental',
];

const Discover = () => {
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get('genre') || 'All';
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState(initialGenre);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchSongs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre]);

  const fetchSongs = async (reset = false) => {
    try {
      setLoading(true);
      const skip = reset ? 0 : page * 20;
      const params = { limit: 20, skip };

      if (selectedGenre !== 'All') params.genre = selectedGenre;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/songs`, { params });

      if (reset) {
        setSongs(response.data);
        setPage(1);
      } else {
        setSongs([...songs, ...response.data]);
        setPage(page + 1);
      }

      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Failed to fetch songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSongs(true);
  };

  return (
    <Layout>
      <div className="py-6 md:py-8 px-4 md:px-8 pb-40">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FFE500] font-bold mb-2">Explore</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white">Discover Music</h1>
            <p className="text-[#A0A0A0] mt-2">Find worship & gospel songs from artists around the world.</p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative mb-6">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#666]" />
            <input
              type="text"
              placeholder="Search songs, artists, lyrics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-bar"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2 rounded-full bg-[#FFE500] text-black font-bold text-sm hover:bg-[#F0D800] transition-all"
              data-testid="search-btn"
            >
              Search
            </button>
          </form>

          {/* Pill Filters */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-2 scrollbar-hide" data-testid="genre-filters">
            {GENRES.map((genre) => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`pill-btn flex-shrink-0 ${
                  selectedGenre === genre ? 'pill-btn-active' : 'pill-btn-inactive'
                }`}
                data-testid={`genre-pill-${genre.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Results Header */}
          <div className="flex items-center justify-between mb-5 mt-4">
            <h2 className="text-xl font-bold text-white">
              {selectedGenre === 'All' ? 'All Songs' : selectedGenre}
            </h2>
            <span className="text-sm text-[#A0A0A0]">{songs.length} results</span>
          </div>

          {/* Results */}
          {loading && songs.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[#FFE500]" />
            </div>
          ) : songs.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {songs.map((song) => (
                  <SongCard key={song.id} song={song} playlist={songs} />
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-12">
                  <button
                    onClick={() => fetchSongs()}
                    disabled={loading}
                    className="pill-btn pill-btn-inactive"
                    data-testid="load-more-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24 bg-[#16181D] rounded-3xl">
              <Music className="h-16 w-16 mx-auto mb-4 text-[#666]" />
              <h3 className="text-xl font-bold text-white mb-2">No Songs Found</h3>
              <p className="text-[#A0A0A0] mb-6">
                {searchQuery || selectedGenre !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Be the first to upload worship music!'}
              </p>
              <Link to="/register">
                <button className="pill-btn pill-btn-active">Upload Your Music</button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Discover;
